<template>
    <div>
        <test-template :testName="testName" :testId="testId" :test="test">
            <template #questions>
                <question-card :question="cQ" v-model="questionResult" :progress="$store.state.tests.currentSlide" :questionnaireLength="$store.state.tests.test.length"  @answerQuestion="() => { $store.commit('tests/incrementSlide') }" @submitQuestionnaire="saveTestResults"></question-card>
            </template>
            <template #results>
                <div key="topGraph" class="first">
                    <h3 class="subtitle-card text-center">{{ $t('tests.testResults') }} - {{ testName.toUpperCase() }}</h3>
                    <column-chart :lastYearResults="$store.state.tests.previousTestResults.slice(-12)" :testName="testName" :customChart="chartValues"></column-chart>
                </div>
                <div key="recapCard" class="second">
                    <h4>{{ $t('tests.results') }} {{ month }}</h4>
                    <b-card class="neu-card mean-result col-lg-7">
                        <h3 class="subtitle-card"> {{ testName }}</h3>
                        <h5 class="subtitle-card" :style="`color: ${resultInfo.color}`"> {{ resultInfo.tag.toUpperCase() }}</h5>
                        <p class="p-white" v-html="resultInfo.resultsAnalysis"></p>
                    </b-card>
                </div>
                <div key="prosCons" class="mt-5">
                    <h4>{{ $t(`tests.mediterranean-diet.${resultInfo.prosCons}.${resultInfo.prosCons}`) }}</h4>
                    <div class="col-lg-12 results-container mt-3">
                        <div v-for="t in resultInfo.listOfProCon" :key="t.name" class="col-lg-8 results-card">
                            <b-col>
                                <b-card class="neu-card" v-b-toggle="t.id" :title="$t(t.name)">
                                    <div>
                                        <div style="margin-top:1%;margin-bottom:-2%">
                                            <span class="text-center accordion-arrow-closed">
                                                <b-icon-chevron-down></b-icon-chevron-down>
                                            </span>
                                            <span class="text-center accordion-arrow-open">
                                                <b-icon-chevron-up></b-icon-chevron-up>
                                            </span>
                                            <b-collapse :id="t.id" class="mt-2 text-left" accordion="accordion">
                                                <p class="card-text text-justify" v-html="$t(t.description)"></p>
                                            </b-collapse>
                                        </div>
                                    </div>
                                </b-card>
                            </b-col>
                        </div>
                    </div>
                </div>
                <div key="tips" class="mt-5">
                    <h4>{{ $t('tests.tips') }}</h4>
                    <span v-html="$t('tests.mediterranean-diet.tips.description')"></span>
                    <b-row class="mt-3">
                        <b-row v-for="n in 7" :key="n" class="col-lg-3 results-card px-2">
                                <b-card class="neu-card">
                                    <b><p class="neu-card-title" v-html="$t(`tests.mediterranean-diet.tips.tip${n}.title`)" :style="`color:${BLUE_COLOUR}`"></p></b>
                                    <span class="card-text text-justify" v-html="$t(`tests.mediterranean-diet.tips.tip${n}.description`)"></span>
                                </b-card>
                        </b-row>
                    </b-row>
                </div>
            </template>
        </test-template>
    </div>
</template>

<script>
import TestTemplate from './TestTemplate.vue'
import { BCollapse } from 'bootstrap-vue'
import { BLUE_COLOUR, GRAY_COLOUR } from '@/constants/colours.js'
import selfEvaluationTestMixin from '@/mixins/selfEvaluationTest.mixin'
import { TEST_MEDITERRANEAN_DIET } from '@/constants/patientServices'
import { formatDateByMonthLong } from '@/helpers/date.helpers'

export default {
    mixins: [selfEvaluationTestMixin],
    components: {
        'test-template': TestTemplate,
        BCollapse,
        'column-chart': () => import('@/components/charts/ColumnChart.vue')
    },
    data () {
        const test = [
            {
                id: 'q1',
                question: this.$t('tests.mediterranean-diet.questions.q1'),
                answer: [
                    {value: 0, text: this.$t('tests.answers.no')},
                    {value: 1, text: this.$t('tests.answers.yes')}
                ],
                result: null
            },
            {
                id: 'q2',
                question: this.$t('tests.mediterranean-diet.questions.q2'),
                answer: [
                    {value: 0, text: this.$t('tests.answers.none')},
                    {value: 0.001, text: '1'},
                    {value: 0.002, text: '2'},
                    {value: 0.003, text: '3'},
                    {value: 1, text: '4'},
                    {value: 1.001, text: '5'},
                    {value: 1.002, text: `6 ${this.$t('tests.answers.orMore')}`}],
                result: null
            },
            {
                id: 'q3',
                question: this.$t('tests.mediterranean-diet.questions.q3'),
                answer: [
                    {value: 0, text: this.$t('tests.answers.none')},
                    {value: 0.001, text: '1'},
                    {value: 1, text: '2'},
                    {value: 1.001, text: '3'},
                    {value: 1.002, text: '4'}
                ],
                result: null
            },
            {
                id: 'q4',
                question: this.$t('tests.mediterranean-diet.questions.q4'),
                answer: [
                    {value: 0, text: this.$t('tests.answers.none')},
                    {value: 0.001, text: '1'},
                    {value: 0.002, text: '2'},
                    {value: 1, text: '3'},
                    {value: 1.001, text: `4 ${this.$t('tests.answers.orMore')}`}
                ],
                result: null
            },
            {
                id: 'q5',
                question: this.$t('tests.mediterranean-diet.questions.q5'),
                answer: [
                    {value: 1, text: `${this.$t('tests.answers.lessThan')} 1`},
                    {value: 0.002, text: '1'},
                    {value: 0.001, text: '2'},
                    {value: 0, text: '3'}
                ],
                result: null
            },
            {
                id: 'q6',
                question: this.$t('tests.mediterranean-diet.questions.q6'),
                answer: [
                    {value: 1, text: `${this.$t('tests.answers.lessThan')} 1`},
                    {value: 0.002, text: '1'},
                    {value: 0.001, text: '2'},
                    {value: 0, text: '3'}
                ],
                result: null
            },
            {
                id: 'q7',
                question: this.$t('tests.mediterranean-diet.questions.q7'),
                answer: [
                    {value: 1, text: `${this.$t('tests.answers.lessThan')} 1`},
                    {value: 0.003, text: '1'},
                    {value: 0.002, text: '2'},
                    {value: 0.001, text: '3'},
                    {value: 0, text: `4 ${this.$t('tests.answers.orMore')}`}
                ],
                result: null
            },
            {
                id: 'q8',
                question: this.$t('tests.mediterranean-diet.questions.q8'),
                answer: [
                    {value: 0, text: this.$t('tests.answers.none')},
                    {value: 0.001, text: '1'},
                    {value: 0.002, text: '2'},
                    {value: 1, text: '3'},
                    {value: 1.001, text: `4 ${this.$t('tests.answers.orMore')}`}
                ],
                result: null
            },
            {
                id: 'q9',
                question: this.$t('tests.mediterranean-diet.questions.q9'),
                answer: [
                    {value: 0, text: this.$t('tests.answers.none')},
                    {value: 0.001, text: '1'},
                    {value: 0.002, text: '2'},
                    {value: 1, text: '3'},
                    {value: 1.001, text: `4 ${this.$t('tests.answers.orMore')}`}
                ],
                result: null
            },
            {
                id: 'q10',
                question: this.$t('tests.mediterranean-diet.questions.q10'),
                answer: [
                    {value: 1.001, text: this.$t('tests.answers.none')},
                    {value: 1, text: '1'},
                    {value: 0.002, text: '2'},
                    {value: 0.001, text: '3'},
                    {value: 0, text: `4 ${this.$t('tests.answers.orMore')}`}
                ],
                result: null
            },
            {
                id: 'q11',
                question: this.$t('tests.mediterranean-diet.questions.q11'),
                answer: [
                    {value: 0, text: this.$t('tests.answers.none')},
                    {value: 0.001, text: '1'},
                    {value: 0.002, text: '2'},
                    {value: 1, text: '3'},
                    {value: 1.001, text: `4 ${this.$t('tests.answers.orMore')}`}
                ],
                result: null
            },
            {
                id: 'q12',
                question: this.$t('tests.mediterranean-diet.questions.q12'),
                answer: [
                    {value: 0, text: this.$t('tests.answers.no')},
                    {value: 1, text: this.$t('tests.answers.yes')}
                ],
                result: null
            },
            {
                id: 'q13',
                question: this.$t('tests.mediterranean-diet.questions.q13'),
                answer: [
                    {value: 0, text: this.$t('tests.answers.none')},
                    {value: 0.001, text: '1'},
                    {value: 1, text: '2'},
                    {value: 1.001, text: '3'},
                    {value: 1.002, text: `4 ${this.$t('tests.answers.orMore')}`}
                ],
                result: null
            }
        ]
        var _this = this
        const chartValues = {
            tooltip: {
                formatter: function () {
                        const adherence = this.point.y < 8 ? _this.$t('tests.mediterranean-diet.scores.lowAdherence.name') : _this.$t('tests.mediterranean-diet.scores.goodAdherence.name')
                        return `<b>${formatDateByMonthLong(this.point.date).replace(/^\w/, c => c.toUpperCase())}</b><br>
                                ${adherence}`
                    }
            },
            yAxis: {
                    title: null,
                    // tickPositions: [0, 8, 13],
                    // tickLength: 1,
                    // labels: {
                    //     formatter: function () {
                    //         return this.value === 0 ? '' : this.value === 8 ? _this.$t('tests.mediterranean-diet.scores.goodAdherence.name') : ''
                    //     }
                    // },
                    labels: {
                        enabled: false
                    },
                    tickInterval: 13,
                    max: 13,
                    gridLineWidth: 0.4,
                    gridLineColor: GRAY_COLOUR
                }
        }

        return {
            test: test,
            testId: TEST_MEDITERRANEAN_DIET,
            testName: this.$t('tests.mediterranean-diet.title'),
            iconSize: this.$uiHelpers.isMobile ? '40' : '50',
            BLUE_COLOUR,
            chartValues: chartValues
        }
    },
    methods: {
        saveTestResults () {
            const result = Math.round(this.test.map(x => x.result).reduce((a, b) => a + b, 0))
            this.save(null, result, this.percentResult(result, 13))
        }
    }
}
</script>


// WEBPACK FOOTER //
// src/components/views/selfEvaluationTests/MediterraneanDietTest.vue